// src/pages/StressAnalyticsPage/StressAnalyticsPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import BarChartComponent from "../../components/results/barChartComponent";
import stressAnalyticsApi from "../../api/stressAnalyticsApi";
import questionnaireScopeApi from "../../api/questionnaireScopeApi";

import ageRanges from "../../data/ageRanges";
import mexicoStates from "../../data/mexicoStates";
import biologicalSexList from "../../data/biologicalSex";
import jobArea from "../../data/jobArea";

import styles from "./StressAnalyticsPage.module.css";

const StressAnalyticsPage = () => {
  // Obtener companyId de params o del state de location
  const { companyId: paramCompanyId, branchId } = useParams();
  const { state } = useLocation();
  const companyId = paramCompanyId || state?.companyId;
  const analyticsScopeId = branchId || companyId;
  const areaScopeId = analyticsScopeId;

  // Para sincronizar filtros con query string
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState({
    jobArea: searchParams.get("jobArea") || "",
    ageRange: searchParams.get("ageRange") || "",
    residenceState: searchParams.get("residenceState") || "",
    biologicalSex: searchParams.get("biologicalSex") || "",
  });

  // Datos empresa
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [companyName, setCompanyName] = useState({});

  // Estados para gráficas
  const [analyticsData, setAnalyticsData] = useState(null);
  const [initialTotal, setInitialTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estados para listado de respondants
  const [respondents, setRespondents] = useState([]);
  const [empSortBy, setEmpSortBy] = useState("email");
  const [empSortOrder, setEmpSortOrder] = useState("asc");

  // Estados para paginación local
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Estado de descarga de Excel
  const [downloading, setDownloading] = useState(false);

  // Opciones de área de trabajo
  const jobAreaOptions = companyJobAreas.length > 0 ? companyJobAreas : jobArea;

  // 1) Cargar datos de la empresa
  useEffect(() => {
    if (!areaScopeId) return;
    (async () => {
      try {
        const scopeResponse = await questionnaireScopeApi.getById(areaScopeId);
        setCompanyName(scopeResponse);
        setCompanyJobAreas(scopeResponse.jobAreas || []);
      } catch (err) {
        console.error("Error cargando empresa/sucursal:", err);
      }
    })();
  }, [areaScopeId]);

  // 2) Cargar datos analíticos al cambiar filtros o companyId
  useEffect(() => {
    if (!analyticsScopeId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await stressAnalyticsApi.getStressAnalyticsByCompanyId(
          analyticsScopeId,
          selectedFilters
        );
        if (initialTotal === null && data?.totalRespondents != null) {
          setInitialTotal(data.totalRespondents);
        }
        setAnalyticsData(data);
      } catch (err) {
        console.error("Error cargando datos analíticos de estrés:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [analyticsScopeId, selectedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3) Cargar listado de respondants al montar la página
  useEffect(() => {
    if (!analyticsScopeId) return;
    (async () => {
      try {
        const list = await stressAnalyticsApi.getStressRespondantsByCompanyId(
          analyticsScopeId
        );
        setRespondents(list);
      } catch (err) {
        console.error("Error obteniendo lista de empleados:", err);
      }
    })();
  }, [analyticsScopeId]);

  // Sorting completo
  const sortedRespondents = useMemo(() => {
    return [...respondents].sort((a, b) => {
      const field = empSortBy;
      if (a[field] < b[field]) return empSortOrder === "asc" ? -1 : 1;
      if (a[field] > b[field]) return empSortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [respondents, empSortBy, empSortOrder]);

  // Paginación: cálculo de páginas y porción a mostrar
  const totalPages = Math.ceil(sortedRespondents.length / pageSize) || 1;
  const displayedRespondents = sortedRespondents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Alternar orden o cambiar campo de ordenamiento
  const onSort = (column) => {
    if (empSortBy === column) {
      setEmpSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setEmpSortBy(column);
      setEmpSortOrder("asc");
    }
  };

  // Flecha de ordenamiento
  const SortArrow = ({ column }) => {
    if (empSortBy !== column) {
      return (
        <span className={styles.sortArrow}>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        </span>
      );
    }
    return (
      <span className={styles.sortArrow}>
        {empSortOrder === "asc" ? (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        )}
      </span>
    );
  };

  // Helper para rendering de filtros
  const renderFilter = (label, name, options) => (
    <div className={styles.filterItem} key={name}>
      <label htmlFor={name} className={styles.filterLabel}>
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={selectedFilters[name]}
        onChange={(e) => {
          const { value } = e.target;
          const newFilters = { ...selectedFilters, [name]: value };
          setSelectedFilters(newFilters);
          const newParams = new URLSearchParams(searchParams);
          value ? newParams.set(name, value) : newParams.delete(name);
          setSearchParams(newParams);
          setCurrentPage(1); // resetear página al cambiar filtro
        }}
        className={styles.select}
      >
        <option value="">Todos</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );

  // Descargar Excel de respondants (estrés)
  const handleDownloadExcel = async () => {
    if (!analyticsScopeId) return;
    try {
      setDownloading(true);
      await stressAnalyticsApi.downloadStressRespondantsByCompanyIdExcel(analyticsScopeId);
    } catch (err) {
      console.error("Error descargando Excel de estrés:", err);
    } finally {
      setDownloading(false);
    }
  };

  const activeFilterCount = Object.values(selectedFilters).filter(Boolean).length;

  const clearFilters = () => {
    setSelectedFilters({
      jobArea: "",
      ageRange: "",
      residenceState: "",
      biologicalSex: "",
    });
    setSearchParams(new URLSearchParams());
    setCurrentPage(1);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>ANALÍTICA DE BIENESTAR</span>
          <h2>Estrés y Burnout</h2>
          <p className={styles.subheader}>
            Resultados de <span className={styles.companyId}>{companyName.name || "la empresa"}</span>
          </p>
        </div>
        <div className={styles.summaryCards}>
          {initialTotal !== null && (
            <div className={styles.summaryCard}>
              <span>Participantes iniciales</span>
              <strong>{initialTotal}</strong>
            </div>
          )}
          {!loading && analyticsData && (
            <div className={`${styles.summaryCard} ${styles.summaryCardAccent}`}>
              <span>Resultados visibles</span>
              <strong>{analyticsData.totalRespondents}</strong>
            </div>
          )}
        </div>
      </header>

      <section className={styles.filtersPanel}>
        <div className={styles.filtersHeader}>
          <div>
            <h3>Filtros generales</h3>
            <p>Refina todas las gráficas al mismo tiempo.</p>
          </div>
          <button type="button" className={styles.clearFilters} onClick={clearFilters} disabled={activeFilterCount === 0}>
            Limpiar filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
        <div className={styles.filters}>
          {renderFilter("Área de trabajo", "jobArea", jobAreaOptions)}
          {renderFilter("Rango de edad", "ageRange", ageRanges)}
          {renderFilter("Estado de residencia", "residenceState", mexicoStates)}
          {renderFilter("Sexo biológico", "biologicalSex", biologicalSexList)}
        </div>
      </section>

      <section className={styles.content}>
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.sectionEyebrow}>RESULTADOS</span>
            <h3>Indicadores de estrés laboral</h3>
          </div>
          {!loading && analyticsData && <span className={styles.resultCount}>{analyticsData.totalRespondents} respuestas</span>}
        </div>
        {loading ? (
          <div className={styles.loadingCard}>Cargando gráficas...</div>
        ) : analyticsData ? (
          <div className={styles.charts}>
            <BarChartComponent
              data={analyticsData.hasBurnOut}
              title="Presencia de Burnout"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.exhaustionLevel}
              title="Agotamiento Emocional"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.cynicismLevel}
              title="Desconexión Laboral"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.efficacyLevel}
              title="Eficiencia Laboral"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.physicalStress}
              title="Estrés Físico"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
          </div>
        ) : (
          <p className={styles.message}>No se pudieron cargar los datos.</p>
        )}
      </section>

      {/* Listado de Empleados (Respondants) */}
      <section className={styles.responsesSection}>
        <div className={styles.tableHeader}>
          <div>
            <span className={styles.sectionEyebrow}>DETALLE</span>
            <h3>Listado de empleados</h3>
            <p>{respondents.length} empleados registrados</p>
          </div>
          <button
            className={styles.exportButton}
            onClick={handleDownloadExcel}
            disabled={downloading || !analyticsScopeId}
            title="Exportar listado de empleados (Estrés) a Excel"
          >
            {downloading ? "Descargando..." : "Exportar Excel"}
          </button>
        </div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th onClick={() => onSort("email")} className={styles.clickable}>
                  Correo <SortArrow column="email" />
                </th>
                <th onClick={() => onSort("status")} className={styles.clickable}>
                  Estado <SortArrow column="status" />
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedRespondents.length > 0 ? (
                displayedRespondents.map((r, i) => (
                  <tr key={r.email}>
                    <td>{(currentPage - 1) * pageSize + i + 1}</td>
                    <td>{r.email}</td>
                    <td>{r.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className={styles.message}>
                    No hay empleados para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Controles de paginación + botón Excel */}
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          <span>
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Siguiente
          </button>
        </div>
      </section>
    </div>
  );
};

export default StressAnalyticsPage;
